import { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { MdDragIndicator, MdDeleteOutline, MdEdit } from 'react-icons/md';
import Modal from '../../../../../components/Modal';
import type { Order } from '../../../../../types/apiResponse/order';
import type { ColumnConfig } from '../../constants/defaultColumnsConfig';
import type { CustomColumn } from '../../customColumnTypes';
import { columnEntities, evaluateCustomColumn, getColumnField, validateCustomColumn } from '../../customColumns';
import ColumnValueEditor, { fieldDragType } from './ColumnValueEditor';
import { CustomColumnsWrapper } from './CustomColumnsStyles';

type Props = {
  columns: ColumnConfig[];
  onChange: (columns: ColumnConfig[]) => void;
  closeModal: () => void;
  sampleOrder?: Order;
};

const emptyDefinition = (type: 'text' | 'number' = 'text'): CustomColumn => ({
  version: 1, type, parts: [{ type: 'text', value: '' }],
});
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export default function CustomColumnsModal({ columns, onChange, closeModal, sampleOrder }: Props) {
  const [name, setName] = useState('');
  const [definition, setDefinition] = useState<CustomColumn>(emptyDefinition);
  const [entityIndex, setEntityIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [inputError, setInputError] = useState('');
  const [status, setStatus] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const insertRef = useRef<((path: string) => void) | null>(null);
  const customColumns = columns.filter((column) => column.custom);
  const validationError = validateCustomColumn(definition);
  const hasValue = definition.parts.some((part) => part.type === 'field' || part.value.length > 0);
  const duplicate = columns.some((column) => column.key !== editingKey && normalize(column.label) === normalize(name));
  const nameError = duplicate ? 'Já existe uma coluna com esse nome.' : '';
  const preview = sampleOrder && !validationError ? evaluateCustomColumn(sampleOrder, definition) : undefined;
  const entities = columnEntities.filter((entity) => definition.type === 'text'
    || entity.fields.some((field) => field.numeric));
  const entity = entities[entityIndex] || entities[0];
  const availableFields = entity.fields.filter((field) => (definition.type === 'text' || field.numeric)
    && normalize(field.label).includes(normalize(search)));

  const resetForm = () => {
    setEditingKey(null);
    setName('');
    setDefinition(emptyDefinition(definition.type));
    setEntityIndex(0);
    setSearch('');
    setInputError('');
  };

  const editColumn = (column: ColumnConfig) => {
    if (!column.custom) return;
    setEditingKey(column.key);
    setName(column.label);
    setDefinition({ ...column.custom, parts: column.custom.parts.map((part) => ({ ...part })) });
    const firstField = column.custom.parts.find((part) => part.type === 'field');
    const eligibleEntities = columnEntities.filter((item) => column.custom?.type === 'text'
      || item.fields.some((field) => field.numeric));
    setEntityIndex(Math.max(0, eligibleEntities.findIndex((item) => item.fields.some(
      (field) => firstField?.type === 'field' && field.path === firstField.path,
    ))));
    setSearch('');
    setInputError('');
    setStatus('');
    nameRef.current?.focus();
    nameRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  };

  const saveColumn = () => {
    if (!name.trim() || nameError || validationError) return;
    if (editingKey) {
      onChange(columns.map((column) => column.key === editingKey
        ? { ...column, label: name.trim(), custom: definition } : column));
    } else {
      onChange([{
        key: `custom:${uuid()}`, label: name.trim(), visible: true, fixed: false,
        subItems: [], custom: definition,
      }, ...columns]);
    }
    const action = editingKey ? 'atualizada' : 'adicionada';
    setStatus(`Coluna “${name.trim()}” ${action}. Salve a configuração da tabela para aplicar.`);
    resetForm();
  };

  return (
    <Modal title="Colunas customizadas" showModal closeModal={closeModal} onlyCloseButton
      style={{ width: '900px' }}>
      <CustomColumnsWrapper>
        <p className="muted">
          Combine campos do pedido para montar sua coluna. Arraste os cartões ou clique para inserir no valor.
        </p>
        <div className="form-fields">
          <label>Nome da coluna
            <input ref={nameRef} value={name} maxLength={80} placeholder="Ex.: Código e nome do vendedor"
              onChange={(event) => { setName(event.target.value); setStatus(''); }} />
          </label>
          <label>Tipo do valor
            <select value={definition.type} onChange={(event) => {
              setDefinition(emptyDefinition(event.target.value as 'text' | 'number'));
              setEntityIndex(0); setSearch(''); setInputError(''); setStatus('');
            }}>
              <option value="text">Texto</option><option value="number">Número</option>
            </select>
          </label>
        </div>
        {nameError && <p className="error" role="alert">{nameError}</p>}
        <div className="workspace">
          <div className="field-picker">
            <label>De onde vem o dado?
              <select value={entityIndex} onChange={(event) => {
                setEntityIndex(Number(event.target.value)); setSearch('');
              }}>
                {entities.map((item, index) => <option key={item.label} value={index}>{item.label}</option>)}
              </select>
            </label>
            {entity.label === 'Histórico pedido' && <small className="muted">
              Valores de pedido e faturamento são somados; datas mostram a mais recente; textos reúnem os retornos.
              Outros números precisam coincidir entre os retornos para permitir cálculos.
            </small>}
            <input aria-label="Buscar campo" placeholder="Buscar campo…" value={search}
              onChange={(event) => setSearch(event.target.value)} />
            <div className="available-fields">
              {availableFields.map((field) => (
                <button className="available-field" key={field.path} type="button" draggable
                  title={`Inserir ${field.label}`}
                  onDragStart={(event) => {
                    event.dataTransfer.setData(fieldDragType, field.path);
                    event.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => insertRef.current?.(field.path)}>
                  <MdDragIndicator />{field.label}<small>{field.numeric ? '123' : 'Aa'}</small>
                </button>
              ))}
              {!availableFields.length && <p className="muted empty">Nenhum campo encontrado.</p>}
            </div>
          </div>
          <div className="composer">
            <div className="heading">
              <strong>Valor da coluna</strong>
              <button type="button" className="text-button" disabled={!hasValue} onClick={() => {
                setDefinition(emptyDefinition(definition.type)); setInputError('');
              }}>Limpar valor</button>
            </div>
            <ColumnValueEditor key={editingKey || 'new'} definition={definition} insertRef={insertRef}
              onChange={(parts) => { setDefinition({ ...definition, parts }); setStatus(''); }}
              onError={setInputError} />
            <small>{definition.type === 'text'
              ? 'Escreva entre os cartões para incluir separadores, símbolos ou outros textos.'
              : 'Use números, campos e + - * /. Sem espaços. Multiplicação e divisão são calculadas primeiro.'}</small>
            {inputError && <p className="error" role="alert">{inputError}</p>}
            {!inputError && validationError && hasValue
              && <p className="error" role="status">{validationError}</p>}
            <div className="preview" aria-live="polite">
              <small>{sampleOrder ? `Prévia · pedido ${sampleOrder.NumeroPedido}` : 'Prévia'}</small>
              <strong>{validationError ? 'Monte o valor acima para ver o resultado.'
                : preview ? String(preview.value) : 'Carregue pedidos na listagem para visualizar um exemplo.'}</strong>
              {preview?.error && <p className="error">{preview.error}</p>}
            </div>
            <button type="button" className="primary-button" disabled={!name.trim() || !!nameError || !!validationError}
              onClick={saveColumn}>{editingKey ? 'Salvar' : 'Adicionar coluna'}</button>
            {editingKey && <button type="button" className="text-button" onClick={() => {
              resetForm(); setStatus('');
            }}>Cancelar edição</button>}
          </div>
        </div>
        {status && <p className="status" role="status">{status}</p>}
        <section className="saved-list">
          <div className="heading"><h3>Colunas criadas</h3><small>{customColumns.length} colunas</small></div>
          <small>
            As novas colunas entram no início da configuração e já ficam visíveis.
            Você pode ordenar, fixar ou ocultar antes de salvar.
          </small>
          <div className="saved-columns">
            {!customColumns.length && <p className="muted empty">Suas colunas personalizadas aparecerão aqui.</p>}
            {customColumns.map((column) => (
              <div className="saved-column" key={column.key}>
                <div><strong>{column.label}</strong>
                  <small>{column.custom?.type === 'number' ? 'Número' : 'Texto'} · {column.custom?.parts.map((part) => (
                    part.type === 'text' ? part.value
                      : `${getColumnField(part.path)?.entity} · ${getColumnField(part.path)?.label}`
                  )).join(' ')}</small>
                </div>
                <button type="button" className="icon-button" aria-label={`Editar coluna ${column.label}`}
                  title="Editar coluna" onClick={() => editColumn(column)}><MdEdit size={20} /></button>
                <button type="button" className="icon-button" aria-label={`Remover coluna ${column.label}`}
                  title="Remover coluna" onClick={() => {
                    onChange(columns.filter((item) => item.key !== column.key));
                    if (editingKey === column.key) resetForm();
                    setStatus(`Coluna “${column.label}” removida. Salve a configuração da tabela para aplicar.`);
                  }}><MdDeleteOutline size={20} /></button>
              </div>
            ))}
          </div>
        </section>
        <small>
          As alterações serão aplicadas ao salvar a configuração da tabela.
          Restaurar a configuração padrão também remove as colunas criadas.
        </small>
      </CustomColumnsWrapper>
    </Modal>
  );
}
